import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, TrendingUp, Filter, ExternalLink, Check } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';
import { SEO } from '@/components/seo/SEO';

const seoContent = {
  nl: {
    title: 'Projecten & voorbeelden - Netblitz',
    description: 'Bekijk onze portfolio met professionele websites die resultaat opleveren. Ontdek hoe Netblitz bedrijven helpt groeien.',
    keywords: 'portfolio, webdesign, website voorbeelden, netblitz, referenties, succesverhalen'
  },
  en: {
    title: 'Projects & examples - Netblitz',
    description: 'View our portfolio of professional websites that deliver results. Discover how Netblitz helps businesses grow.',
    keywords: 'portfolio, web design, website examples, netblitz, references, success stories'
  },
  fr: {
    title: 'Projets & exemples - Netblitz',
    description: 'Découvrez notre portfolio de sites web professionnels qui génèrent des résultats.',
    keywords: 'portfolio, conception web, références, succès'
  },
  de: {
    title: 'Projekte & Beispiele - Netblitz',
    description: 'Sehen Sie unser Portfolio professioneller Websites, die Ergebnisse liefern.',
    keywords: 'Portfolio, Webdesign, Referenzen, Erfolgsgeschichten'
  }
};

// Translations
const translations = {
  nl: {
    heroTitle: 'Projecten &',
    heroHighlight: 'voorbeelden',
    heroSubtitle: 'Een greep uit projecten en voorbeelden die we voor klanten hebben gebouwd, met de gebruikte functionaliteiten per sector.',
    filterAll: 'Alles',
    filterHoreca: 'Horeca',
    filterBouw: 'Bouw',
    filterCoaching: 'Coaching',
    filterRetail: 'Retail',
    filterDiensten: 'Diensten',
    viewWebsite: 'Bekijk website',    ctaTitle: 'Wil jij het volgende succesverhaal zijn?',
    ctaSubtitle: 'Neem contact op en ontdek wat Netblitz voor jouw bedrijf kan betekenen.',
    ctaButton: 'Neem contact op',
    result: 'Resultaat',
    projectReview: 'Projectreview',
    noProjects: 'Geen projecten gevonden in deze categorie.'
  },
  en: {
    heroTitle: 'Projects &',
    heroHighlight: 'examples',
    heroSubtitle: 'A selection of projects and examples we have built for clients, with the functionalities used per sector.',
    filterAll: 'All',
    filterHoreca: 'Hospitality',
    filterBouw: 'Construction',
    filterCoaching: 'Coaching',
    filterRetail: 'Retail',
    filterDiensten: 'Services',
    viewWebsite: 'View website',
    ctaTitle: 'Want to be the next success story?',
    ctaSubtitle: 'Contact us and discover what Netblitz can do for your business.',
    ctaButton: 'Contact us',
    result: 'Result',
    projectReview: 'Project review',
    noProjects: 'No projects found in this category.'
  },
  fr: {
    heroTitle: 'Nos',
    heroHighlight: 'réussites',
    heroSubtitle: 'Découvrez comment nous aidons les entreprises à se développer avec des sites web qui génèrent des résultats.',
    filterAll: 'Tout',
    filterHoreca: 'Hôtellerie',
    filterBouw: 'Construction',
    filterCoaching: 'Coaching',
    filterRetail: 'Commerce',
    filterDiensten: 'Services',
    viewWebsite: 'Voir le site',
    ctaTitle: 'Voulez-vous être la prochaine réussite?',
    ctaSubtitle: 'Contactez-nous et découvrez ce que Netblitz peut faire pour votre entreprise.',
    ctaButton: 'Contactez-nous',
    result: 'Résultat',
    projectReview: 'Revue du projet',
    noProjects: 'Aucun projet trouvé dans cette catégorie.'
  },
  de: {
    heroTitle: 'Unsere',
    heroHighlight: 'Erfolgsgeschichten',
    heroSubtitle: 'Entdecken Sie, wie wir Unternehmen mit Websites helfen, die echte Ergebnisse liefern.',
    filterAll: 'Alle',
    filterHoreca: 'Gastronomie',
    filterBouw: 'Bauwesen',
    filterCoaching: 'Coaching',
    filterRetail: 'Einzelhandel',
    filterDiensten: 'Dienstleistungen',
    viewWebsite: 'Website ansehen',
    ctaTitle: 'Möchten Sie die nächste Erfolgsgeschichte sein?',
    ctaSubtitle: 'Kontaktieren Sie uns und erfahren Sie, was Netblitz für Ihr Unternehmen tun kann.',
    ctaButton: 'Kontaktieren Sie uns',
    result: 'Ergebnis',
    projectReview: 'Projektbewertung',
    noProjects: 'Keine Projekte in dieser Kategorie gefunden.'
  }
};

// Portfolio projects with results
const projects = [
  {
    id: 1, slug: null, name: 'HomeCareService', sector: 'all',
    sectorLabel: { nl: 'Wasserij & textielverzorging', en: 'Laundry & textile care', fr: 'Blanchisserie & textile', de: 'Wäscherei & Textilpflege' },
    image: 'https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=800&q=72&auto=format',
    website: 'home-care-service.eu',
    description: {
      nl: 'Website voor professionele wasserij- en textielverzorgingsdiensten voor particuliere en zakelijke klanten.',
      en: 'Website for professional laundry and textile care services for both private and business customers.',
      fr: 'Site pour des services professionnels de blanchisserie et d\'entretien textile pour particuliers et entreprises.',
      de: 'Website für professionelle Wäscherei- und Textilpflegedienste für Privat- und Geschäftskunden.'
    },
    features: {
      nl: ['Duidelijke dienstpresentatie', 'B2C én B2B doelgroep', 'Offerte- en contactmogelijkheden', 'Mobielvriendelijk', 'SEO-structuur'],
      en: ['Clear service presentation', 'B2C and B2B audience', 'Quote & contact options', 'Mobile-friendly', 'SEO structure'],
      fr: ['Présentation claire des services', 'Public B2C et B2B', 'Devis & contact', 'Adapté au mobile', 'Structure SEO'],
      de: ['Klare Servicedarstellung', 'B2C- und B2B-Zielgruppe', 'Angebot & Kontakt', 'Mobilfreundlich', 'SEO-Struktur']
    },
    review: {
      nl: 'Voor HomeCareService ontwikkelde Netblitz een professionele digitale omgeving waarin particuliere en zakelijke wasserijdiensten overzichtelijk worden gepresenteerd, met focus op gebruiksgemak, aanvragen en verdere online groei.',
      en: 'For HomeCareService, Netblitz built a professional digital environment that clearly presents private and business laundry services, focused on ease of use, requests and further online growth.',
      fr: 'Pour HomeCareService, Netblitz a développé un environnement digital professionnel présentant clairement les services de blanchisserie, axé sur la facilité d\'usage et la croissance en ligne.',
      de: 'Für HomeCareService entwickelte Netblitz eine professionelle digitale Umgebung, die private und geschäftliche Wäschedienste übersichtlich präsentiert.'
    },
    color: 'from-sky-500 to-cyan-600'
  },
  {
    id: 2, slug: null, name: 'Jet-Express', sector: 'all',
    sectorLabel: { nl: 'Koeriersdienst', en: 'Courier service', fr: 'Service de coursier', de: 'Kurierdienst' },
    image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=800&q=72&auto=format',
    website: 'jet-express.eu',
    description: {
      nl: 'Digitale oplossing voor een moderne koeriersdienst, met online prijsberekening en een volledige aanvraagflow.',
      en: 'Digital solution for a modern courier service, with online price calculation and a full request flow.',
      fr: 'Solution digitale pour un service de coursier moderne, avec calcul de prix en ligne et flux de demande complet.',
      de: 'Digitale Lösung für einen modernen Kurierdienst, mit Online-Preisberechnung und komplettem Anfrageprozess.'
    },
    features: {
      nl: ['Professionele website', 'Online prijsberekening', 'Aanvraagflow', 'Verschillende vervoersopties', 'Mobiele optimalisatie', 'Schaalbare structuur'],
      en: ['Professional website', 'Online price calculation', 'Request flow', 'Multiple transport options', 'Mobile optimization', 'Scalable structure'],
      fr: ['Site professionnel', 'Calcul de prix en ligne', 'Flux de demande', 'Options de transport', 'Optimisation mobile', 'Structure évolutive'],
      de: ['Professionelle Website', 'Online-Preisberechnung', 'Anfrageprozess', 'Verschiedene Transportoptionen', 'Mobile Optimierung', 'Skalierbare Struktur']
    },
    review: {
      nl: 'Jet-Express toont dat Netblitz meer bouwt dan een standaard bedrijfswebsite: een schaalbare digitale oplossing met online prijsberekening en geautomatiseerde aanvragen.',
      en: 'Jet-Express shows Netblitz builds more than a standard business website: a scalable digital solution with online pricing and automated requests.',
      fr: 'Jet-Express montre que Netblitz construit plus qu\'un site standard : une solution évolutive avec calcul de prix et demandes automatisées.',
      de: 'Jet-Express zeigt, dass Netblitz mehr als eine Standard-Website baut: eine skalierbare Lösung mit Online-Preisberechnung und automatisierten Anfragen.'
    },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 3, slug: null, name: 'Clean-Force', sector: 'all',
    sectorLabel: { nl: 'Schoonmaakdiensten', en: 'Cleaning services', fr: 'Services de nettoyage', de: 'Reinigungsdienste' },
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=72&auto=format',
    website: 'clean-force.eu',
    description: {
      nl: 'Professionele website voor een schoonmaakbedrijf gericht op zowel zakelijke als particuliere dienstverlening.',
      en: 'Professional website for a cleaning company focused on both business and private services.',
      fr: 'Site professionnel pour une entreprise de nettoyage, services aux professionnels et particuliers.',
      de: 'Professionelle Website für ein Reinigungsunternehmen für Geschäfts- und Privatkunden.'
    },
    features: {
      nl: ['Overzichtelijke dienstpresentatie', 'Zakelijke positionering', 'Offerteaanvragen', 'Lokale/regionale vindbaarheid', 'Duidelijke call-to-actions'],
      en: ['Clear service overview', 'Business positioning', 'Quote requests', 'Local/regional visibility', 'Clear calls-to-action'],
      fr: ['Aperçu clair des services', 'Positionnement B2B', 'Demandes de devis', 'Visibilité locale/régionale', 'Appels à l\'action clairs'],
      de: ['Übersichtliche Servicedarstellung', 'B2B-Positionierung', 'Angebotsanfragen', 'Lokale/regionale Sichtbarkeit', 'Klare Call-to-Actions']
    },
    review: {
      nl: 'Voor Clean-Force realiseerde Netblitz een overzichtelijke, SEO-gerichte website die de schoonmaakdiensten professioneel presenteert met duidelijke offerteaanvragen.',
      en: 'For Clean-Force, Netblitz delivered a clear, SEO-focused website that presents the cleaning services professionally with clear quote requests.',
      fr: 'Pour Clean-Force, Netblitz a réalisé un site clair et optimisé SEO présentant les services de nettoyage avec des demandes de devis claires.',
      de: 'Für Clean-Force realisierte Netblitz eine übersichtliche, SEO-orientierte Website mit klaren Angebotsanfragen.'
    },
    color: 'from-emerald-500 to-teal-600'
  },
  {
    id: 4, slug: null, name: 'De Sint aan Huis', sector: 'all',
    sectorLabel: { nl: 'Events & seizoensdienst', en: 'Events & seasonal', fr: 'Événements & saisonnier', de: 'Events & saisonal' },
    image: 'https://images.unsplash.com/photo-1576267423445-b2e0074d68a4?w=800&q=72&auto=format',
    website: 'desintaanhuis.eu',
    description: {
      nl: 'Gespecialiseerde website voor het aanvragen en organiseren van Sinterklaasbezoeken, met een online aanvraagproces.',
      en: 'Specialized website for requesting and organizing Sinterklaas visits, with an online request process.',
      fr: 'Site spécialisé pour demander et organiser des visites de Saint-Nicolas, avec processus de demande en ligne.',
      de: 'Spezialisierte Website zum Anfragen und Organisieren von Nikolausbesuchen, mit Online-Anfrageprozess.'
    },
    features: {
      nl: ['Duidelijke dienstpresentatie', 'Online aanvraagproces', 'Info voor gezinnen en organisaties', 'Gebruiksvriendelijke navigatie', 'Seizoensgerichte opzet'],
      en: ['Clear service presentation', 'Online request process', 'Info for families and organizations', 'User-friendly navigation', 'Seasonal setup'],
      fr: ['Présentation claire', 'Demande en ligne', 'Infos familles & organisations', 'Navigation conviviale', 'Approche saisonnière'],
      de: ['Klare Präsentation', 'Online-Anfrage', 'Infos für Familien & Organisationen', 'Benutzerfreundliche Navigation', 'Saisonaler Aufbau']
    },
    review: {
      nl: 'Voor De Sint aan Huis bouwde Netblitz een gebruiksvriendelijke, seizoensgerichte website waarmee bezoeken eenvoudig digitaal kunnen worden aangevraagd en verwerkt.',
      en: 'For De Sint aan Huis, Netblitz built a user-friendly, seasonal website that lets visits be requested and processed digitally with ease.',
      fr: 'Pour De Sint aan Huis, Netblitz a construit un site saisonnier convivial permettant de demander et traiter les visites en ligne.',
      de: 'Für De Sint aan Huis baute Netblitz eine benutzerfreundliche, saisonale Website zur einfachen digitalen Anfrage und Bearbeitung von Besuchen.'
    },
    color: 'from-red-500 to-rose-600'
  },
  {
    id: 5, slug: null, name: 'Tip Top Group', sector: 'all',
    sectorLabel: { nl: 'Corporate', en: 'Corporate', fr: 'Corporate', de: 'Corporate' },
    image: 'https://images.unsplash.com/photo-1624213012413-fda54df1810f?w=800&q=72&auto=format',
    website: 'tiptopgroup.eu',
    description: {
      nl: 'Corporate website waarin verschillende activiteiten binnen één professionele groepsstructuur worden gepresenteerd.',
      en: 'Corporate website presenting multiple activities within one professional group structure.',
      fr: 'Site corporate présentant plusieurs activités au sein d\'une structure de groupe professionnelle.',
      de: 'Corporate-Website, die mehrere Aktivitäten innerhalb einer professionellen Gruppenstruktur präsentiert.'
    },
    features: {
      nl: ['Corporate uitstraling', 'Duidelijke bedrijfsstructuur', 'Presentatie van meerdere activiteiten', 'Professionele merkpositionering', 'Schaalbare opbouw'],
      en: ['Corporate look & feel', 'Clear company structure', 'Multiple activities presented', 'Professional brand positioning', 'Scalable build'],
      fr: ['Image corporate', 'Structure d\'entreprise claire', 'Plusieurs activités présentées', 'Positionnement de marque', 'Structure évolutive'],
      de: ['Corporate-Auftritt', 'Klare Unternehmensstruktur', 'Mehrere Aktivitäten', 'Professionelle Markenpositionierung', 'Skalierbarer Aufbau']
    },
    review: {
      nl: 'Voor Tip Top Group ontwikkelde Netblitz een corporate website met een professionele uitstraling die de verschillende activiteiten binnen de groep helder samenbrengt.',
      en: 'For Tip Top Group, Netblitz developed a corporate website with a professional look that clearly brings together the group\'s various activities.',
      fr: 'Pour Tip Top Group, Netblitz a développé un site corporate professionnel réunissant clairement les activités du groupe.',
      de: 'Für Tip Top Group entwickelte Netblitz eine corporate Website, die die verschiedenen Aktivitäten der Gruppe klar zusammenführt.'
    },
    color: 'from-slate-600 to-slate-800'
  },
  {
    id: 6, slug: null, name: 'Tip Top Property', sector: 'all',
    sectorLabel: { nl: 'Vastgoed', en: 'Real estate', fr: 'Immobilier', de: 'Immobilien' },
    image: 'https://images.unsplash.com/photo-1605146769289-440113cc3d00?w=800&q=72&auto=format',
    website: 'tiptopproperty.eu',
    description: {
      nl: 'Website gericht op vastgoed en aanverwante dienstverlening, met een professionele visuele presentatie en contactmogelijkheden.',
      en: 'Website focused on real estate and related services, with professional visual presentation and contact options.',
      fr: 'Site axé sur l\'immobilier et les services associés, avec présentation visuelle professionnelle et contact.',
      de: 'Website mit Fokus auf Immobilien und verwandte Dienste, mit professioneller visueller Präsentation und Kontakt.'
    },
    features: {
      nl: ['Vastgoed-/pandpresentatie', 'Professionele visuele opzet', 'Contact- en aanvraagmogelijkheden', 'Mobielvriendelijke ervaring', 'Uitbreidbare structuur'],
      en: ['Property presentation', 'Professional visual setup', 'Contact & request options', 'Mobile-friendly experience', 'Extensible structure'],
      fr: ['Présentation des biens', 'Mise en page professionnelle', 'Contact & demandes', 'Expérience mobile', 'Structure extensible'],
      de: ['Immobilienpräsentation', 'Professionelles Layout', 'Kontakt & Anfragen', 'Mobilfreundlich', 'Erweiterbare Struktur']
    },
    review: {
      nl: 'Voor Tip Top Property realiseerde Netblitz een verzorgde vastgoedwebsite met een professionele visuele presentatie en duidelijke contactmogelijkheden, opgezet om verder uitgebreid te kunnen worden.',
      en: 'For Tip Top Property, Netblitz delivered a polished real estate website with professional visuals and clear contact options, set up to be extended further.',
      fr: 'Pour Tip Top Property, Netblitz a réalisé un site immobilier soigné avec une présentation visuelle professionnelle et des options de contact claires.',
      de: 'Für Tip Top Property realisierte Netblitz eine gepflegte Immobilien-Website mit professioneller Präsentation und klaren Kontaktmöglichkeiten.'
    },
    color: 'from-teal-600 to-emerald-700'
  }
];

// Filter categories
const filterCategories = [
  { id: 'all', labelKey: 'filterAll' },
];

// Translation for view case study
const viewCaseStudyLabels = {
  nl: 'Bekijk case study',
  en: 'View case study',
  fr: 'Voir l\'étude de cas',
  de: 'Fallstudie ansehen'
};

// Project Card Component
const ProjectCard = ({ project, language, t, index }) => {
  const caseStudyPath = project.slug ? `/${language}/portfolio/${project.slug}` : null;
  
  // Wrapper component - Link if case study exists, div otherwise
  const CardWrapper = ({ children }) => {
    if (caseStudyPath) {
      return (
        <Link to={caseStudyPath} className="block">
          {children}
        </Link>
      );
    }
    return <>{children}</>;
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group relative"
      data-testid={`project-card-${project.id}`}
    >
      <CardWrapper>
        {/* Image Container */}
        <div className="relative overflow-hidden rounded-2xl aspect-[4/3] mb-4">
          {/* Screenshot Image */}
          <img 
            src={project.image} 
            alt={project.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Gradient Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-t ${project.color} opacity-0 group-hover:opacity-80 transition-opacity duration-300`} />
          
          {/* Hover Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-all duration-300 p-6">
            {caseStudyPath ? (
              <span className="flex items-center gap-2 bg-white text-slate-900 font-semibold py-3 px-6 rounded-full shadow-lg">
                {viewCaseStudyLabels[language] || viewCaseStudyLabels.nl}
                <ArrowRight size={16} />
              </span>
            ) : (
              <a
                href={`https://${project.website}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 bg-white text-slate-900 font-semibold py-3 px-6 rounded-full shadow-lg hover:bg-slate-100 transition-colors"
              >
                {t.viewWebsite}
                <ExternalLink size={16} />
              </a>
            )}
          </div>
          
          {/* Sector Badge */}
          <div className="absolute top-4 left-4">
            <span className="bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              {project.sectorLabel[language] || project.sectorLabel.nl}
            </span>
          </div>
          
          {/* Case Study indicator */}
          {caseStudyPath && (
            <div className="absolute top-4 right-4">
              <span className="bg-[#2563EB] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                Case Study
              </span>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="space-y-3">
          {/* Client Name */}
          <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#2563EB] transition-colors">
            {project.name}
          </h3>

          {/* Website Link */}
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.open(`https://${project.website}`, '_blank', 'noopener,noreferrer'); }}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#2563EB] transition-colors cursor-pointer"
          >
            {project.website}
            <ArrowUpRight size={14} />
          </span>

          {/* Description */}
          {project.description && (
            <p className="text-sm text-slate-600 leading-relaxed">
              {project.description[language] || project.description.nl}
            </p>
          )}

          {/* Key delivered features */}
          {project.features && (
            <ul className="space-y-1.5 pt-1">
              {(project.features[language] || project.features.nl).map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check size={15} className="text-[#2563EB] flex-shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Project review (Netblitz project description, not a client testimonial) */}
          {project.review && (
            <div className="mt-3 border-l-2 border-[#2563EB]/30 pl-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-1">
                {t.projectReview}
              </p>
              <p className="text-sm text-slate-600 italic leading-relaxed">
                "{project.review[language] || project.review.nl}"
              </p>
            </div>
          )}
        </div>
      </CardWrapper>
    </motion.div>
  );
};

const PortfolioPage = () => {
  const { language } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  
  const t = translations[language] || translations.nl;
  const seo = seoContent[language] || seoContent.nl;
  const contactPath = getLocalizedPath('contact', language);
  
  // Filter projects
  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.sector === activeFilter);

  return (
    <main data-testid="portfolio-page">
      <SEO 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#F3F4F6] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] font-semibold text-sm px-4 py-2 rounded-full mb-6"
            >
              <TrendingUp size={16} />
              {language === 'nl' ? 'Projecten & voorbeelden' : language === 'fr' ? 'Projets & exemples' : language === 'de' ? 'Projekte & Beispiele' : 'Projects & examples'}
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#111827] mb-6 tracking-tight">
              {t.heroTitle}{' '}
              <span className="bg-gradient-to-r from-[#2563EB] to-blue-600 bg-clip-text text-transparent">
                {t.heroHighlight}
              </span>
            </h1>
            
            <p className="text-xl text-[#4B5563] leading-relaxed">
              {t.heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 bg-white border-b border-slate-100 sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex items-center gap-2 text-slate-400 mr-2">
              <Filter size={18} />
            </div>
            {filterCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === category.id
                    ? 'bg-[#2563EB] text-white shadow-md shadow-blue-500/20'
                    : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-gray-200'
                }`}
                data-testid={`filter-${category.id}`}
              >
                {t[category.labelKey]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            layout
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, index) => (
                <ProjectCard 
                  key={project.id} 
                  project={project} 
                  language={language}
                  t={t}
                  index={index}
                />
              ))}
            </AnimatePresence>
          </motion.div>
          
          {/* Empty State */}
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <p className="text-slate-500 text-lg">
                {t.noProjects}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              {t.ctaTitle}
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              {t.ctaSubtitle}
            </p>
            <Link
              to={contactPath}
              className="group inline-flex items-center gap-3 bg-white text-[#2563EB] font-semibold text-lg py-4 px-8 rounded-full shadow-[0_8px_30px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] hover:-translate-y-1 transition-all duration-300"
              data-testid="portfolio-cta-button"
            >
              {t.ctaButton}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default PortfolioPage;
